import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { browserLocalPersistence, getAuth, initializeAuth } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import {
  getStorage,
  uploadBytes,
  getDownloadURL,
  ref,
  listAll,
  getBlob,
  deleteObject,
} from "firebase/storage";
import {
  documentId,
  getCountFromServer,
  query,
  where,
  type CollectionReference,
} from "firebase/firestore";
import { validateImageSize } from "./common";

import { connectorConfig, uptimeCountBulk } from "@firebasegen/default-connector";
import { getDataConnect, connectDataConnectEmulator } from "firebase/data-connect";
import { connectFirestoreEmulator } from "firebase/firestore";
import { connectAuthEmulator } from "firebase/auth";
import { connectStorageEmulator } from "firebase/storage";
import { connectFunctionsEmulator } from "firebase/functions";
// @ts-ignore
import { sentryClient } from "@/sentry";

// Overridden in test mode (.env.test) so the emulators run under a `demo-*` project
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || "mimblysoftware";

const firebaseConfig = {
  apiKey: "AIzaSyCxaHNSTbu0pxFdA52oa_g8TDNbMs8uXwY",
  authDomain: `${projectId}.firebaseapp.com`,
  projectId,
  storageBucket: `${projectId}.appspot.com`,
  messagingSenderId: "54080610234",
  appId: "1:54080610234:web:43ac69f7bb518b05fa8783",
  measurementId: "G-KTGPS1894E",
};

const isTest = process.env.NODE_ENV === "test" || import.meta.env?.MODE === "test";
// Tests must never be able to reach a real project, even if the emulators are misconfigured
if (isTest && !projectId.startsWith("demo-")) {
  throw new Error(`Test mode requires a "demo-*" Firebase project, got "${projectId}"`);
}

export const fb = initializeApp(firebaseConfig);
// @ts-ignore
export const db = getFirestore(fb, isTest ? undefined : "(default)");
export const functions = getFunctions(fb, "europe-west1");
// Tests persist auth in localStorage (not IndexedDB) so Cypress clears it between tests
export const auth = isTest
  ? initializeAuth(fb, { persistence: browserLocalPersistence })
  : getAuth(fb);
export const storage = getStorage(fb);
export const dataConnect = getDataConnect(connectorConfig);

const isDev = import.meta.env.DEV || false;
if (isTest || isDev) {
  const emulatorHost = window.location.hostname || "localhost";
  console.log("Connecting to Firebase Emulators...");
  // Dev emulator ports by default; tests run their own emulators on other ports (.env.test)
  const port = (value: string | undefined, fallback: number) => Number(value) || fallback;
  const env = import.meta.env;
  connectFirestoreEmulator(db, emulatorHost, port(env.VITE_EMULATOR_FIRESTORE_PORT, 8080));
  connectAuthEmulator(auth, `http://${emulatorHost}:${port(env.VITE_EMULATOR_AUTH_PORT, 9099)}`);
  connectStorageEmulator(storage, emulatorHost, port(env.VITE_EMULATOR_STORAGE_PORT, 9199));
  connectFunctionsEmulator(functions, emulatorHost, port(env.VITE_EMULATOR_FUNCTIONS_PORT, 5001));
  connectDataConnectEmulator(
    dataConnect,
    emulatorHost,
    port(env.VITE_EMULATOR_DATACONNECT_PORT, 9399),
  );
}

export const signout = async (): Promise<[boolean, Error | null]> => {
  try {
    // Sign out from Firebase
    await auth.signOut();
    return [true, null];
  } catch (error) {
    console.error("Error during Firebase signout:", error);
    return [false, error as Error];
  }
};

export async function uploadImage(
  path: string,
  file: File,
): Promise<[string | null, Error | null]> {
  try {
    if (!file || !(file instanceof File)) {
      throw new Error(`Unable to proccess image`);
    }

    if (!validateImageSize(file)) {
      throw new Error(`${file.name}, file size too big.`);
    }

    // Get the file extension from the original file
    const fileExtension = file.name.split(".").pop() || "bin";
    const fileName = `${Date.now()}_${(Math.random() * 100).toFixed()}.${fileExtension}`;
    const imageRef = ref(storage, `${path}/${fileName}`);
    const snapshot = await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return [downloadURL, null];
  } catch (error) {
    console.error(error);
    return [null, error as Error];
  }
}

const uptimePromiseCache: Record<string, Promise<Record<string, number | null>>> = {};

export async function uptime(
  mmids: string[],
  interval?: { from: Date; to: Date },
): Promise<Record<string, number | null>> {
  const NORMAL_RUNNING_MODES = [0, 4, 8, 5, 6];
  if (!mmids.length) {
    throw new Error("Missing mmid parameter");
  }
  const cacheKey = `${mmids.slice().sort().join(",")}-${interval?.from?.getTime()}-${interval?.to?.getTime()}`;
  if (cacheKey in uptimePromiseCache) {
    return uptimePromiseCache[cacheKey];
  }

  const promise = (async () => {
    let from: Date = new Date();
    from.setFullYear(2000);
    let to: Date = new Date();
    if (interval) {
      from = interval.from;
      to = interval.to;
    }
    const { data } = await uptimeCountBulk({
      mmids,
      from: from.toISOString(),
      to: to.toISOString(),
    });
    const baseCalc: Record<string, { error: number; normal: number }> = {};
    data.statsAfterResetViews.forEach((stat) => {
      const mmid = stat.mmid;
      const errmask = Number(stat.errmask) || 0;
      if (!mmid) return;
      if (!baseCalc[mmid]) {
        baseCalc[mmid] = {
          error: 0,
          normal: 0,
        };
      }
      if (errmask > 0 && stat.runMode && !NORMAL_RUNNING_MODES.includes(stat.runMode)) {
        baseCalc[mmid].error++;
      } else {
        baseCalc[mmid].normal++;
      }
    });

    const calc: Record<string, number | null> = {};
    Object.entries(baseCalc).forEach(([mmid, { error, normal }]) => {
      const total = error + normal;
      if (total === 0) {
        calc[mmid] = null;
      } else {
        calc[mmid] = Number((100 - (error / total) * 100).toFixed(0));
      }
    });
    return calc;
  })();

  uptimePromiseCache[cacheKey] = promise;
  try {
    return await promise;
  } finally {
    // Keep it in cache for a brief moment to absorb Vue reactivity spam
    setTimeout(() => {
      delete uptimePromiseCache[cacheKey];
    }, 200);
  }
}

export async function fetchFoldersAndFiles(
  path: string,
): Promise<
  [Array<{ name: string; files: Array<{ name: string; url: string }> }> | null, Error | null]
> {
  try {
    const rootRef = ref(storage, path);
    const result = await listAll(rootRef);
    const folderPromises = result.prefixes.map(async (folderRef) => {
      const folderName = folderRef.name;
      const filesResult = await listAll(folderRef);

      const filePromises = filesResult.items.map(async (fileRef) => {
        const url = await getDownloadURL(fileRef);
        return {
          name: fileRef.name,
          url: url,
        };
      });

      const files = await Promise.all(filePromises);
      return {
        name: folderName,
        files: files,
      };
    });

    return [await Promise.all(folderPromises), null];
  } catch (error) {
    console.error("Error fetching folders and files:", error);
    return [null, error as Error];
  }
}

export async function downloadFile(
  url: string,
  filename: string,
): Promise<[boolean | null, Error | null]> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    return [true, null];
  } catch (error) {
    console.error("Error downloading file:", error);
    return [null, error as Error];
  }
}

export async function exists(
  collection: CollectionReference,
  key: string,
  val: any,
): Promise<boolean> {
  const qKey = key === "id" ? documentId() : key;
  const snap = await getCountFromServer(query(collection, where(qKey, "==", val)));
  return !!snap.data().count;
}

export async function getTranslations() {
  const enRef = ref(storage, "mimsights/translations/en.json");
  const metadataRef = ref(storage, "mimsights/translations/metadata.json");

  const [enBlob, metadataBlob] = await Promise.all([getBlob(enRef), getBlob(metadataRef)]);

  const enText = await enBlob.text();
  const metadataText = await metadataBlob.text();

  const en = JSON.parse(enText);
  const metadata = JSON.parse(metadataText);

  return { en, metadata };
}

export async function deleteTranslations() {
  const enRef = ref(storage, "mimsights/translations/en.json");
  const metadataRef = ref(storage, "mimsights/translations/metadata.json");

  return await Promise.all([deleteObject(enRef), deleteObject(metadataRef)]);
}

export async function uploadTranslations({ en, metadata }: { en: any; metadata: any }) {
  const enRef = ref(storage, "mimsights/translations/en.json");
  const metadataRef = ref(storage, "mimsights/translations/metadata.json");

  const enBlob = new Blob([JSON.stringify(en)], { type: "application/json" });
  const metadataBlob = new Blob([JSON.stringify(metadata)], {
    type: "application/json",
  });

  return await Promise.all([uploadBytes(enRef, enBlob), uploadBytes(metadataRef, metadataBlob)]);
}
